import React, { useState, useEffect } from "react";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { getAbsoluteImageUrl, normalizeImageUrl } from "@/utils/imageUtils";
import {
  Image,
  Upload,
  Save,
  DollarSign,
  Loader2,
  Camera,
  Home,
  ShoppingBag,
  Book,
  RefreshCw,
  X,
  ExternalLink,
  Link2,
  Store,
  Users,
} from "lucide-react";

// Base sections that are always shown
const baseFotoSections = [
  {
    id: "hero",
    title: "Imagen Principal (Hero)",
    description: "La imagen de fondo del inicio. Recomendado: 1920x1080px",
    icon: Home,
  },
];

const historiaSections = [
  {
    id: "historia_1",
    title: "Historia - Imagen 1",
    description: "Primera imagen de la sección Nuestra Historia",
    icon: Book,
  },
  {
    id: "historia_2",
    title: "Historia - Imagen 2",
    description: "Segunda imagen de la sección Nuestra Historia",
    icon: Book,
  },
  {
    id: "historia_3",
    title: "Historia - Imagen 3",
    description: "Tercera imagen de la sección Nuestra Historia",
    icon: Book,
  },
  {
    id: "historia_4",
    title: "Historia - Imagen 4",
    description: "Cuarta imagen de la sección Nuestra Historia",
    icon: Book,
  },
];

// Secciones adicionales del landing
const additionalSections = [
  {
    id: "catalogo_bg_1",
    title: "Catálogo Digital - Fondo Card 1",
    description: "Imagen decorativa para el recuadro 'Catálogo Directo' (sutil, con opacidad)",
    icon: Store,
  },
  {
    id: "catalogo_bg_2",
    title: "Catálogo Digital - Fondo Card 2",
    description: "Imagen decorativa para el recuadro 'Vendedores Autorizados' (sutil, con opacidad)",
    icon: Store,
  },
  {
    id: "unete_bg",
    title: "Únete al Equipo - Fondo",
    description: "Imagen de fondo para la sección de beneficios de vendedores (sutil)",
    icon: Users,
  },
  {
    id: "unete_cta_bg",
    title: "Únete al Equipo - Fondo CTA",
    description: "Imagen de fondo para el recuadro de llamada a la acción final",
    icon: Users,
  },
];

// Helper to normalize product name to a valid key
const normalizeProductName = (name) => {
  return name.toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/á/g, "a")
    .replace(/é/g, "e")
    .replace(/í/g, "i")
    .replace(/ó/g, "o")
    .replace(/ú/g, "u")
    .replace(/ñ/g, "n");
};

export default function AdminConfigLanding() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [domicilio, setDomicilio] = useState(7000);
  const [fotos, setFotos] = useState({});
  const [uploading, setUploading] = useState({});
  const [productosConfig, setProductosConfig] = useState([]);
  const [fotoSections, setFotoSections] = useState([...baseFotoSections, ...historiaSections, ...additionalSections]);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // State for URL input mode
  const [urlInputMode, setUrlInputMode] = useState({});
  const [urlInputValue, setUrlInputValue] = useState({});
  
  // Convert relative URLs to absolute URLs for display (uses shared utility)
  const getAbsoluteUrl = (url) => getAbsoluteImageUrl(url, API_URL);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const [configRes, comisionesRes] = await Promise.all([
        api.get("/configuracion/landing"),
        api.get("/comisiones/configuracion")
      ]);
      
      setDomicilio(configRes.data.costo_domicilio || 7000);
      setFotos(configRes.data.fotos || {});
      
      // Generate product sections from commission config
      const productos = comisionesRes.data.comisiones_productos || [];
      setProductosConfig(productos);
      
      // Build dynamic foto sections
      const productoSections = productos
        .filter(p => p.activo)
        .map((producto, index) => ({
          id: `producto_${normalizeProductName(producto.producto_nombre)}`,
          title: `Producto - ${producto.producto_nombre}`,
          description: `Imagen para ${producto.producto_nombre} ($${producto.precio_venta?.toLocaleString('es-CO')})`,
          icon: ShoppingBag,
          orden: producto.orden_landing || index + 1
        }))
        .sort((a, b) => a.orden - b.orden);
      
      setFotoSections([...baseFotoSections, ...productoSections, ...historiaSections, ...additionalSections]);
    } catch (error) {
      console.error("Error fetching config:", error);
      toast.error("Error al cargar la configuración");
    } finally {
      setLoading(false);
    }
  };

  // Compress image before upload
  const compressImage = (file, maxWidth = 1920, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = document.createElement('img');
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // Scale down if too large
          if (width > maxWidth) {
            height = Math.round(height * maxWidth / width);
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob((blob) => {
            resolve(new File([blob], file.name, { type: 'image/jpeg' }));
          }, 'image/jpeg', quality);
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  };

  const handleFileUpload = async (tipo, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type - be more permissive with initial check
    const allowedExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.bmp'];
    const fileName = file.name.toLowerCase();
    const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
    
    if (!file.type.startsWith("image/") && !hasValidExtension) {
      toast.error("Solo se permiten archivos de imagen (JPG, PNG, WEBP, GIF)");
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(`El archivo es demasiado grande (${(file.size / (1024*1024)).toFixed(1)}MB). Máximo 10MB`);
      return;
    }

    setUploading(prev => ({ ...prev, [tipo]: true }));

    try {
      let fileToUpload = file;
      
      // Compress if larger than 1MB
      if (file.size > 1 * 1024 * 1024) {
        toast.info("Comprimiendo imagen...");
        fileToUpload = await compressImage(file, 1920, 0.85);
        console.log(`Compressed from ${(file.size/1024).toFixed(0)}KB to ${(fileToUpload.size/1024).toFixed(0)}KB`);
      }

      const formData = new FormData();
      formData.append("file", fileToUpload);

      console.log(`Uploading to /configuracion/landing/foto/${tipo}, file: ${fileToUpload.name}, size: ${fileToUpload.size}`);
      
      const response = await api.post(`/configuracion/landing/foto/${tipo}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000, // 3 minute timeout for large files
        maxBodyLength: Infinity,
        maxContentLength: Infinity,
      });
      
      console.log(`Upload successful for ${tipo}`);
      setFotos(prev => ({ ...prev, [tipo]: response.data.url }));
      toast.success(`Imagen "${tipo}" actualizada correctamente`);
    } catch (error) {
      console.error("Upload error:", error);
      console.error("Error details:", {
        message: error.message,
        code: error.code,
        response: error.response?.status,
        data: error.response?.data
      });
      
      // More specific error messages
      if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
        toast.error("La subida tardó demasiado. Intente con una imagen más pequeña (máximo 2MB recomendado).");
      } else if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
        toast.error("Error de red. Verifique su conexión e intente de nuevo.");
      } else if (error.response?.status === 413) {
        toast.error("El archivo es demasiado grande para el servidor. Máximo 10MB.");
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.detail || "Formato de imagen no válido. Use JPG, PNG, WEBP o GIF.");
      } else if (error.response?.status === 500) {
        toast.error("Error del servidor al procesar la imagen. Intente con otra imagen más pequeña.");
      } else if (error.response?.status === 502 || error.response?.status === 504) {
        toast.error("El servidor no respondió a tiempo. Intente con una imagen más pequeña.");
      } else {
        toast.error(error.response?.data?.detail || `Error al subir la imagen (${error.message || 'desconocido'}). Intente con una imagen más pequeña.`);
      }
    } finally {
      setUploading(prev => ({ ...prev, [tipo]: false }));
      // Reset file input
      event.target.value = '';
    }
  };

  const handleRemoveFoto = (tipo) => {
    setFotos(prev => {
      const newFotos = { ...prev };
      delete newFotos[tipo];
      return newFotos;
    });
    toast.info(`Imagen "${tipo}" eliminada. Guarda para aplicar cambios.`);
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      // Normalize all image URLs before saving
      const normalizedFotos = {};
      for (const [key, url] of Object.entries(fotos)) {
        normalizedFotos[key] = normalizeImageUrl(url) || url;
      }
      
      await api.post("/configuracion/landing", {
        costo_domicilio: parseInt(domicilio),
        fotos: normalizedFotos,
      });
      
      // Update local state with normalized URLs
      setFotos(normalizedFotos);
      toast.success("Configuración guardada correctamente");
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Error al guardar la configuración");
    } finally {
      setSaving(false);
    }
  };

  // Handle URL input for external images (Imgur, etc.)
  const handleUrlInput = (tipo, url) => {
    const normalizedUrl = normalizeImageUrl(url);
    if (normalizedUrl) {
      setFotos(prev => ({ ...prev, [tipo]: normalizedUrl }));
      setUrlInputMode(prev => ({ ...prev, [tipo]: false }));
      setUrlInputValue(prev => ({ ...prev, [tipo]: "" }));
      toast.success(`URL de imagen guardada para "${tipo}"`);
    } else {
      toast.error("URL de imagen inválida");
    }
  };

  const toggleUrlInputMode = (tipo) => {
    setUrlInputMode(prev => ({ ...prev, [tipo]: !prev[tipo] }));
    setUrlInputValue(prev => ({ ...prev, [tipo]: "" }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-torta-brown" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-config-landing">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-display text-3xl text-torta-dark">Configuración Landing Page</h1>
          <p className="text-torta-muted">Personaliza las imágenes y precios de tu página web</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchConfig} variant="outline" className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Recargar
          </Button>
          <Button onClick={handleSaveConfig} disabled={saving} className="gap-2 bg-torta-brown hover:bg-torta-brown/90">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Guardar Todo
          </Button>
        </div>
      </div>

      {/* Precio Domicilio */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h2 className="font-display text-xl text-torta-dark">Precio de Domicilio</h2>
            <p className="text-sm text-torta-muted">Costo de envío para pedidos en Bogotá</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex-1 max-w-xs">
            <Label htmlFor="domicilio">Valor en pesos (COP)</Label>
            <Input
              id="domicilio"
              type="number"
              value={domicilio}
              onChange={(e) => setDomicilio(e.target.value)}
              className="mt-1"
            />
          </div>
          <div className="text-2xl font-display text-torta-brown">
            ${parseInt(domicilio || 0).toLocaleString("es-CO")}
          </div>
        </div>
      </div>

      {/* Fotos por Sección */}
      <div className="bg-white rounded-2xl p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-torta-cream rounded-lg flex items-center justify-center">
            <Image className="w-5 h-5 text-torta-brown" />
          </div>
          <div>
            <h2 className="font-display text-xl text-torta-dark">Imágenes del Sitio</h2>
            <p className="text-sm text-torta-muted">Sube las fotos para personalizar tu landing page</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {fotoSections.map((section) => {
            const Icon = section.icon;
            const hasImage = fotos[section.id];
            const isUploading = uploading[section.id];
            const isUrlMode = urlInputMode[section.id];

            return (
              <div
                key={section.id}
                className="border-2 border-dashed border-torta-sand rounded-xl p-4 hover:border-torta-brown/50 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Icon className="w-5 h-5 text-torta-brown" />
                    <div>
                      <h3 className="font-medium text-torta-dark">{section.title}</h3>
                      <p className="text-xs text-torta-muted">{section.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => toggleUrlInputMode(section.id)}
                      className={`p-1.5 rounded transition-colors ${isUrlMode ? 'bg-blue-100 text-blue-600' : 'text-torta-muted hover:text-torta-brown hover:bg-torta-cream'}`}
                      title="Usar URL externa (Imgur, etc.)"
                    >
                      <Link2 className="w-4 h-4" />
                    </button>
                    {hasImage && (
                      <button
                        onClick={() => handleRemoveFoto(section.id)}
                        className="text-red-500 hover:text-red-700 p-1.5"
                        title="Eliminar imagen"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* URL Input Mode */}
                {isUrlMode && (
                  <div className="mb-3 p-3 bg-blue-50 rounded-lg">
                    <Label className="text-xs text-blue-700 mb-1 block">URL de imagen (Imgur, etc.)</Label>
                    <div className="flex gap-2">
                      <Input
                        type="url"
                        placeholder="https://i.imgur.com/XXXXX.jpg"
                        value={urlInputValue[section.id] || ""}
                        onChange={(e) => setUrlInputValue(prev => ({ ...prev, [section.id]: e.target.value }))}
                        className="flex-1 text-sm h-9"
                      />
                      <Button
                        size="sm"
                        onClick={() => handleUrlInput(section.id, urlInputValue[section.id])}
                        disabled={!urlInputValue[section.id]}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        Usar
                      </Button>
                    </div>
                    <p className="text-xs text-blue-600 mt-1">
                      Tip: Para Imgur usa <code className="bg-blue-100 px-1 rounded">i.imgur.com/XXX.jpg</code>
                    </p>
                  </div>
                )}

                {hasImage ? (
                  <div className="relative group">
                    <img
                      src={getAbsoluteUrl(fotos[section.id])}
                      alt={section.title}
                      className="w-full h-40 object-cover rounded-lg"
                      onError={(e) => {
                        e.target.style.opacity = '0.3';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="absolute inset-0 bg-red-50 rounded-lg items-center justify-center text-red-500 text-sm hidden">
                      Error al cargar imagen
                    </div>
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                      <label className="cursor-pointer bg-white text-torta-dark px-3 py-2 rounded-lg font-medium flex items-center gap-2 hover:bg-torta-cream text-sm">
                        <Camera className="w-4 h-4" />
                        Subir
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif,image/bmp,.jpg,.jpeg,.png,.webp,.gif,.bmp"
                          onChange={(e) => handleFileUpload(section.id, e)}
                          className="hidden"
                        />
                      </label>
                      {fotos[section.id]?.startsWith('http') && (
                        <a
                          href={getAbsoluteUrl(fotos[section.id])}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-white text-torta-dark px-3 py-2 rounded-lg font-medium flex items-center gap-2 hover:bg-torta-cream text-sm"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Ver
                        </a>
                      )}
                    </div>
                  </div>
                ) : (
                  <label className="cursor-pointer block">
                    <div className="h-40 bg-torta-cream/50 rounded-lg flex flex-col items-center justify-center hover:bg-torta-cream transition-colors">
                      {isUploading ? (
                        <Loader2 className="w-8 h-8 animate-spin text-torta-brown" />
                      ) : (
                        <>
                          <Upload className="w-8 h-8 text-torta-brown/50 mb-2" />
                          <span className="text-sm text-torta-muted">Clic para subir imagen</span>
                          <span className="text-xs text-torta-muted/70">o usa el ícono 🔗 para URL externa</span>
                        </>
                      )}
                    </div>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif,image/bmp,.jpg,.jpeg,.png,.webp,.gif,.bmp"
                      onChange={(e) => handleFileUpload(section.id, e)}
                      className="hidden"
                      disabled={isUploading}
                    />
                  </label>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Preview Link */}
      <div className="bg-torta-cream rounded-2xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-display text-lg text-torta-dark">Vista Previa</h3>
            <p className="text-sm text-torta-muted">Abre la página en otra pestaña para ver los cambios</p>
          </div>
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-torta-brown hover:underline font-medium"
          >
            Ver Landing Page →
          </a>
        </div>
      </div>
    </div>
  );
}
